// AtlasHoverCard — Story #6837 Task #6904 (the card in screen-M4-atlas-sector-lens.png).
//
// Shown on the Sector lens when the pointer is on an invention's footprint, dots or star: its
// name, primary sector, "Also" secondary sectors, the sector value for the chosen country, and a
// link to its Market tab. The assessment score and modelled SOM in the mockup's card are not on
// the own-scope data this lens reads (they would mean decrypting every owned project on every
// Atlas load), so they are not drawn; the Market tab carries both.
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import { sectorColor } from '../../lib/sectorColors'

export interface HoverCardSector { section: string; name: string | null }

export interface AtlasHoverCardProps {
  projectId: string
  name: string
  /** null: this invention has not been categorised yet. */
  primary: HoverCardSector | null
  also: HoverCardSector[]
  /** e.g. "US$95.6bn (2025)"; null when no official value is loaded for the primary sector. */
  value: string | null
  countryLabel: string
  onClose: () => void
}

const label = (s: HoverCardSector) => `${s.section}${s.name ? ` ${s.name}` : ''}`

export default function AtlasHoverCard({ projectId, name, primary, also, value, countryLabel, onClose }: AtlasHoverCardProps) {
  return (
    <div
      data-testid="atlas-hover-card"
      role="dialog"
      aria-label={`${name}: sector summary`}
      className="absolute left-2 top-2 z-10 w-64 rounded-lg border border-[rgba(12,74,110,0.12)] bg-white/95 p-3 text-xs text-gray-600 shadow-md"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-bold text-iflow-900" data-testid="atlas-hover-name">{name}</p>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded p-0.5 text-gray-400 hover:text-gray-700">
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      {primary ? (
        <>
          <p className="mt-1" data-testid="atlas-hover-primary">
            <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: sectorColor(primary.section) }} aria-hidden />
            <span className="font-semibold text-gray-700">{label(primary)}</span>
          </p>
          {also.length > 0 && (
            <p className="mt-1" data-testid="atlas-hover-also"><span className="font-semibold text-gray-500">Also:</span> {also.map(label).join(' · ')}</p>
          )}
          <p className="mt-1" data-testid="atlas-hover-value">
            <span className="font-semibold text-gray-500">Sector value · {countryLabel}:</span> {value ?? '—'}
          </p>
        </>
      ) : (
        <p className="mt-1" data-testid="atlas-hover-uncategorised">Not categorised yet.</p>
      )}
      <Link to={`/project/${projectId}?tab=market`} data-testid="atlas-hover-open" className="mt-2 inline-block font-semibold text-iflow-700 underline">
        Open Market tab →
      </Link>
    </div>
  )
}
